/**
 * #2663 お店のオーナーの 2 段階認証 (TOTP) が必須かどうか (`STORE_TOTP_MODE`)。
 *
 * `/webauthn/config` から 1 回だけ取り、画面全体で共有する (RequireStoreAuth の gate と、
 * 設定画面の StoreTotpManager の両方が見る)。取れなければ optional として扱う
 * (画面で塞がなくても API が 403 で止める)。
 */
import { useEffect, useState } from 'react'
import { api } from '../services/api'

export type StoreTotpMode = 'optional' | 'required_all'

let cached: StoreTotpMode | null = null
let inflight: Promise<StoreTotpMode> | null = null
// 取れなかったとき、しばらく取りに行かない (店舗画面はルートごとに RequireStoreAuth が mount するので、
// API が落ちている間に遷移のたびに取り直してスピナーがちらつく)
let failedUntil = 0

function load(): Promise<StoreTotpMode> {
  if (cached) return Promise.resolve(cached)
  if (Date.now() < failedUntil) return Promise.resolve('optional')
  if (!inflight) {
    inflight = api
      .getWebauthnConfig()
      .then((c) => {
        cached = c.storeTotpMode === 'required_all' ? 'required_all' : 'optional'
        return cached
      })
      .catch((): StoreTotpMode => {
        failedUntil = Date.now() + 60_000
        return 'optional'
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/** `null` = まだ取れていない (gate はその間は待つ)。 */
export function useStoreTotpMode(): StoreTotpMode | null {
  const [mode, setMode] = useState<StoreTotpMode | null>(cached)
  useEffect(() => {
    let cancelled = false
    load().then((m) => {
      if (!cancelled) setMode(m)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return mode
}
